/**
 * Augmentation Registry - defines all available augmentations
 */
import type { Augmentation } from "../types/augmentation";

/** All available augmentations in the game */
export const AUGMENTATION_REGISTRY: ReadonlyMap<string, Augmentation> = new Map(
  [
    {
      id: "extended_knight",
      name: "Extended Knight",
      nameKo: "확장 나이트",
      description:
        "Knights can also step 1 square in any of 8 directions (like a King), in addition to the normal L-shaped jump. Example: a Knight on e4 can also move to e5, d4, or f3.",
      descriptionKo:
        "나이트가 기존 L자 점프에 더해 킹처럼 모든 방향으로 1칸 추가 이동 가능합니다. 예: e4의 나이트는 e5, d4, f3 등 인접 칸으로도 이동 가능.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["KNIGHT"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "KING_STEP",
      },
    },
    {
      id: "swift_pawn",
      name: "Swift Pawn",
      nameKo: "신속 폰",
      description:
        "Pawns can always move 2 squares forward from any position, not just from their starting rank. The path must be clear. Note: a 2-square advance from a non-starting rank does NOT enable en passant.",
      descriptionKo:
        "폰이 시작 랭크가 아니어도 항상 2칸 전진 가능합니다. 경로가 비어 있어야 합니다. 주의: 시작 랭크 외에서의 2칸 전진은 앙파상을 허용하지 않습니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "FORWARD_2",
      },
    },
    {
      id: "diagonal_rook",
      name: "Diagonal Rook",
      nameKo: "대각 룩",
      description:
        "Rooks can also jump exactly 2 squares diagonally (not sliding — the square in between is ignored). Example: a Rook on e4 can jump to g6, g2, c6, or c2, even if the intermediate square is occupied.",
      descriptionKo:
        "룩이 대각선으로 정확히 2칸 점프 이동 가능합니다 (슬라이드가 아니며 중간 칸은 무시됩니다). 예: e4의 룩은 g6, g2, c6, c2로 점프 가능 (중간 칸이 막혀도 됩니다).",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["ROOK"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "DIAGONAL_2",
      },
    },
    {
      id: "lateral_bishop",
      name: "Lateral Bishop",
      nameKo: "직선 비숍",
      description:
        "Bishops can also jump exactly 2 squares horizontally or vertically (not sliding — the square in between is ignored). Example: a Bishop on e4 can jump to e6, e2, g4, or c4, even if the intermediate square is occupied.",
      descriptionKo:
        "비숍이 가로 또는 세로로 정확히 2칸 점프 이동 가능합니다 (슬라이드가 아니며 중간 칸은 무시됩니다). 예: e4의 비숍은 e6, e2, g4, c4로 점프 가능 (중간 칸이 막혀도 됩니다).",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["BISHOP"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "ORTHOGONAL_2",
      },
    },
    {
      id: "mobile_king",
      name: "Mobile King",
      nameKo: "기동 킹",
      description:
        "King can also jump exactly 2 squares horizontally or vertically (in addition to the normal 1-square movement). The square in between is ignored. Example: from e1, the King can jump to e3 or g1. This is NOT castling. Note: using this jump sets hasMoved, permanently removing castling rights.",
      descriptionKo:
        "킹이 기존 1칸 이동에 더해 가로 또는 세로로 정확히 2칸 추가 이동 가능합니다. 중간 칸은 무시됩니다. 예: e1의 킹은 e3 또는 g1로 점프 가능. 이 이동은 캐슬링이 아닙니다. 주의: 이 점프를 사용하면 hasMoved가 설정되어 캐슬링 권리를 영구적으로 잃습니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["KING"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "ORTHOGONAL_2",
      },
    },
    {
      id: "acceleration",
      name: "Acceleration",
      nameKo: "가속 승진",
      description:
        "Pawns can promote one rank earlier: white pawns promote when entering rank 7 (instead of 8), black pawns when entering rank 2 (instead of 1). Example: a white pawn moving from e6 to e7 can immediately promote to a Queen.",
      descriptionKo:
        "폰이 한 칸 일찍 승진 가능합니다. 백 폰은 7랭크 진입 시 (기본: 8랭크), 흑 폰은 2랭크 진입 시 (기본: 1랭크) 승진합니다. 예: e6의 백 폰이 e7로 이동 시 즉시 퀸으로 승진 가능.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "PROMOTION_RANK_OVERRIDE",
        whiteRank: 7,
        blackRank: 2,
      },
    },
    {
      id: "PN-003",
      name: "Charging Pawn",
      nameKo: "돌격 폰",
      description:
        "Pawns can advance 3 squares forward on their first move (from starting rank), in addition to the normal 1 or 2-square options. The full path must be clear. Note: a 3-square advance does NOT enable en passant. Example: a white pawn on e2 can move to e5 if e3, e4, and e5 are all empty.",
      descriptionKo:
        "폰이 시작 랭크에서 첫 이동 시 기존 1칸·2칸 전진에 더해 3칸 전진 옵션이 추가됩니다. 경로가 완전히 비어 있어야 합니다. 주의: 3칸 전진은 앙파상을 허용하지 않습니다. 예: e2의 백 폰은 e3, e4, e5가 모두 비어 있으면 e5로 이동 가능.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "FORWARD_3",
      },
    },
    {
      id: "RK-002",
      name: "Extended Rook",
      nameKo: "확장 룩",
      description:
        "Rooks can also jump exactly 2 squares horizontally or vertically (in addition to normal orthogonal sliding). The intermediate square is ignored. Example: a Rook on e4 can jump to e6, e2, g4, or c4, even if the square in between is occupied. Useful for bypassing pieces that block the rook's sliding path.",
      descriptionKo:
        "룩이 기존 직선 슬라이드에 더해 가로 또는 세로로 정확히 2칸 점프 이동 가능합니다. 중간 칸은 무시됩니다. 예: e4의 룩은 e6, e2, g4, c4로 점프 가능 (중간 칸이 막혀도 됩니다). 경로를 막는 기물을 우회할 때 유용합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["ROOK"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "ORTHOGONAL_2",
      },
    },
    {
      id: "BP-002",
      name: "Swift Bishop",
      nameKo: "신속 비숍",
      description:
        "Bishops can also jump exactly 2 squares diagonally (in addition to normal diagonal sliding). The intermediate square is ignored. Example: a Bishop on e4 can jump to g6, g2, c6, or c2, even if the intermediate square is occupied. Useful for bypassing pieces that block the bishop's diagonal path.",
      descriptionKo:
        "비숍이 기존 대각선 슬라이드에 더해 대각선으로 정확히 2칸 점프 이동 가능합니다. 중간 칸은 무시됩니다. 예: e4의 비숍은 g6, g2, c6, c2로 점프 가능 (중간 칸이 막혀도 됩니다). 경로를 막는 기물을 우회할 때 유용합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["BISHOP"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "DIAGONAL_2",
      },
    },
    {
      id: "PN-011",
      name: "Pawn Legion",
      nameKo: "폰 군단",
      description:
        "Start the game with 2 extra pawns placed on a3 and h3 (white) or a6 and h6 (black). The extra pawns have already moved — they cannot advance 2 squares and do not create en passant threats. Example: white starts with 10 pawns total; the a3 and h3 pawns can only advance 1 square on their first move.",
      descriptionKo:
        "게임 시작 시 a3와 h3(백) 또는 a6와 h6(흑)에 폰 2개가 추가됩니다. 추가 폰은 이미 이동한 것으로 간주되어 2칸 전진 및 앙파상 대상이 불가합니다. 예: 백은 총 10개의 폰으로 시작하며, a3와 h3 폰은 첫 이동으로 a4나 h4로 전진 가능합니다.",
      category: "RESOURCE",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "INITIAL_PLACEMENT",
        placements: [
          {
            pieceType: "PAWN",
            whitePosition: { file: "a", rank: 3 },
            blackPosition: { file: "a", rank: 6 },
            hasMoved: true,
          },
          {
            pieceType: "PAWN",
            whitePosition: { file: "h", rank: 3 },
            blackPosition: { file: "h", rank: 6 },
            hasMoved: true,
          },
        ],
      },
    },
    {
      id: "PN-002",
      name: "Sturdy Pawn",
      nameKo: "견고한 폰",
      description:
        "Pawns can also capture enemy pieces directly ahead (1 square forward, same file), in addition to the normal diagonal capture. The square ahead must be occupied by an enemy piece. Example: a white pawn on e4 can capture an enemy piece on e5.",
      descriptionKo:
        "폰이 대각선 포획에 더해 바로 앞(같은 파일, 1칸 전방)의 적 기물도 포획 가능합니다. 앞 칸에 적 기물이 있어야 합니다. 예: e4의 백 폰은 e5의 적 기물을 포획 가능합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "FORWARD_CAPTURE_1",
      },
    },
    {
      id: "AL-014",
      name: "Strategic Retreat",
      nameKo: "전략적 후퇴",
      description:
        "Pawns can move 1 square backward to an empty square (no capture). Useful for repositioning a pawn that has advanced too far. Example: a white pawn on e5 can retreat to e4 if the square is empty.",
      descriptionKo:
        "폰이 빈 칸으로 1칸 후퇴 이동 가능합니다 (포획 불가). 과도하게 전진한 폰을 재배치할 때 유용합니다. 예: e5의 백 폰은 e4가 비어 있으면 e4로 후퇴 가능합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "BACKWARD_1",
      },
    },
    {
      id: "AL-018",
      name: "Still Defense",
      nameKo: "정적 방어",
      description:
        "Pieces that have never moved cannot be captured. Once a piece moves for the first time, it loses this protection permanently. The King is never protected by this effect. Example: at game start, white's Queen on d1 cannot be captured — but as soon as it moves, it becomes vulnerable.",
      descriptionKo:
        "한 번도 이동하지 않은 기물은 포획될 수 없습니다. 기물이 처음 이동하는 순간 보호가 영구적으로 사라집니다. 킹에게는 적용되지 않습니다. 예: 게임 시작 시 d1의 퀸은 포획 불가 — 하지만 이동하는 순간 취약해집니다.",
      category: "DEFENSE",
      targetPieceTypes: [],
      effect: {
        type: "UNMOVED_IMMUNITY",
      },
    },
    {
      id: "KG-003",
      name: "King Fortress",
      nameKo: "킹 요새",
      description:
        "King can also jump exactly 2 squares diagonally (in addition to normal 1-square movement). The intermediate square is ignored. Example: from e1, the King can jump to c3, g3, c-1 (off board), or g-1 (off board) — so c3 and g3 are the valid options. Note: using this jump sets hasMoved, permanently removing castling rights.",
      descriptionKo:
        "킹이 기존 1칸 이동에 더해 대각선으로 정확히 2칸 점프 이동 가능합니다. 중간 칸은 무시됩니다. 예: e1의 킹은 c3 또는 g3로 점프 가능. 주의: 이 점프를 사용하면 hasMoved가 설정되어 캐슬링 권리를 영구적으로 잃습니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["KING"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "DIAGONAL_2",
      },
    },
    {
      id: "AL-001",
      name: "Guardian",
      nameKo: "수호자",
      description:
        "Pieces adjacent to your King (within 1 square) cannot be captured by the opponent. Protection is active each turn as long as pieces remain next to the King. Example: if your Rook is on f1 and your King is on e1, the opponent cannot capture the Rook.",
      descriptionKo:
        "킹 인접 1칸 이내의 아군 기물은 상대방이 포획할 수 없습니다. 기물이 킹 옆에 있는 한 매 턴 보호가 유지됩니다. 예: 룩이 f1, 킹이 e1에 있으면 상대는 그 룩을 포획할 수 없습니다.",
      category: "DEFENSE",
      targetPieceTypes: [],
      effect: {
        type: "KING_ADJACENT_IMMUNITY",
        radius: 1,
      },
    },
    {
      id: "ghost_bishop",
      name: "Ghost Bishop",
      nameKo: "유령 비숍",
      description:
        "Bishop passes through all pieces and can land on any enemy piece along its diagonal path (except the King). Cannot give check. Useful for capturing pieces protected by or behind other pieces.",
      descriptionKo:
        "비숍이 모든 기물을 통과하며 대각선 경로상의 적 기물에 착지할 수 있습니다 (킹 제외). 체크를 줄 수 없습니다. 다른 기물에 의해 보호받거나 뒤에 있는 기물을 잡는 데 유용합니다.",
      category: "PHASE_MOVEMENT",
      targetPieceTypes: ["BISHOP"],
      effect: {
        type: "PHASE_MOVEMENT",
      },
    },
    {
      id: "pattern_swap",
      name: "Pattern Swap",
      nameKo: "패턴 교환",
      description:
        "Bishop and Knight swap their movement patterns. Bishops gain the Knight's L-shaped jump (2+1 squares), while Knights gain the Bishop's full diagonal sliding movement. Example: a 'Bishop' with this augment jumps like a knight from e4 to f6. Note: players whose piece was a Knight benefit more, as diagonal sliding is generally stronger than an L-jump.",
      descriptionKo:
        "비숍과 나이트의 이동 패턴이 교환됩니다. 비숍은 나이트의 L자 점프(2+1칸)를 얻고, 나이트는 비숍의 대각선 슬라이드를 얻습니다. 예: e4의 '비숍'은 나이트처럼 f6으로 점프 가능. 주의: 나이트를 가진 플레이어가 더 유리합니다 (대각선 슬라이드가 L자 점프보다 강력).",
      category: "MOVEMENT_SWAP",
      targetPieceTypes: ["BISHOP", "KNIGHT"],
      effect: {
        type: "MOVEMENT_SWAP",
        swapPairs: [{ pieceA: "BISHOP", pieceB: "KNIGHT" }],
      },
    },
    {
      id: "AL-021",
      name: "Reverse",
      nameKo: "역전",
      description:
        "Pawns can move 1 square backward to an empty square (no capture). Queens are limited to a maximum of 3 squares in any direction. Example: a Pawn on d4 can retreat to d3. A Queen on d1 can reach at most d4 (3 squares forward). Note: pawn backward movement does NOT trigger promotion and does NOT grant en passant rights.",
      descriptionKo:
        "폰이 빈 칸으로 1칸 후진 이동 가능합니다 (포획 불가). 퀸의 이동은 모든 방향에서 최대 3칸으로 제한됩니다. 예: d4의 폰은 d3으로 후진 가능. d1의 퀸은 최대 d4까지만 이동 가능. 주의: 폰 후진은 프로모션 및 앙파상 권리를 부여하지 않습니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN", "QUEEN"],
      effect: {
        type: "REVERSE_MOVEMENT",
      },
    },
    {
      id: "PN-007",
      name: "Delayed Promotion",
      nameKo: "지연 프로모션",
      description:
        "When a pawn promotes, it automatically becomes a Knight instead of any chosen piece, and a second Knight is placed on the nearest adjacent empty square (same rank preferred). If no adjacent square is available, only one Knight is placed. Example: a pawn promoting on e8 becomes a Knight on e8, and another Knight appears on d8 or f8 (if empty).",
      descriptionKo:
        "폰이 프로모션 랭크에 도달하면 선택 없이 자동으로 나이트가 되며, 인접한 빈 칸(같은 랭크 우선)에 두 번째 나이트가 배치됩니다. 빈 인접 칸이 없으면 나이트 1개만 배치됩니다. 예: e8에서 프로모션 시 e8에 나이트, d8 또는 f8(빈 경우)에 두 번째 나이트 배치.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "DUAL_KNIGHT_PROMOTION",
      },
    },
    {
      id: "PN-006",
      name: "Sacrifice",
      nameKo: "희생",
      description:
        "Whenever one of your pawns is captured, the most-advanced remaining ally pawn instantly becomes a Queen (no promotion rank required). Applies each time a pawn is captured. If no other pawns remain, the effect does not trigger. Example: if your d4-pawn is captured and your most-advanced pawn is on e6, the e6-pawn instantly becomes a Queen on e6.",
      descriptionKo:
        "아군 폰이 포획될 때마다, 남아있는 아군 폰 중 가장 전진한 폰이 즉시 퀸으로 변환됩니다 (프로모션 랭크 불필요). 폰이 없으면 효과 미발동. 예: d4 폰이 잡히고 가장 전진한 폰이 e6에 있으면, e6 폰이 즉시 퀸이 됩니다.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "SACRIFICE",
      },
    },
    {
      id: "KG-005",
      name: "King's Fury",
      nameKo: "킹의 분노",
      description:
        "The King can capture pieces that are normally protected by defensive augmentations (Guardian's king-adjacent immunity, Still Defense's unmoved immunity). All other pieces are unaffected. Example: if the opponent's Rook is protected by Guardian (adjacent to their King), your King can still capture it even though no other piece can.",
      descriptionKo:
        "킹이 수비 증강으로 보호된 기물(수호자의 킹 인접 면역, 정적 방어의 미이동 면역)도 포획할 수 있습니다. 다른 기물에는 영향 없음. 예: 상대 룩이 수호자 효과로 보호되어도, 아군 킹은 해당 룩을 포획 가능합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["KING"],
      effect: {
        type: "KING_FURY",
      },
    },
    {
      id: "RK-005",
      name: "Domination Rook",
      nameKo: "지배 룩",
      description:
        "Rooks can pass through the first enemy piece they encounter (capturing it en route) and continue to any empty square or a second enemy piece beyond it (also captured). Own pieces still block the rook normally. Example: a Rook on a1 with an enemy on a4 can move to a5, a6, a7 (capturing the a4 piece en route), or to a7 (capturing both a4 and a7 if an enemy is there). The standard single-capture move (landing on a4) is still available.",
      descriptionKo:
        "룩이 경로 상의 첫 번째 적 기물을 통과하며 포획하고, 그 너머 빈 칸 또는 두 번째 적 기물(역시 포획)로 이동 가능합니다. 아군 기물은 여전히 룩을 차단합니다. 예: a1의 룩과 a4에 적이 있으면, 룩은 a5/a6/a7로 이동해 a4 기물을 경유 포획하거나 a7에 적이 있으면 두 기물을 동시에 포획. 일반 단일 포획(a4에 착지)도 여전히 가능합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["ROOK"],
      effect: {
        type: "ROOK_LINE_CAPTURE",
      },
    },
    {
      id: "PN-013",
      name: "Double Promotion",
      nameKo: "더블 프로모션",
      description:
        "When a pawn promotes, it automatically becomes a Bishop instead of any chosen piece, and a Knight is placed on the nearest adjacent empty square (same rank preferred). If no adjacent square is available, only the Bishop is placed. Example: a pawn promoting on e8 becomes a Bishop on e8, and a Knight appears on d8 or f8 (if empty).",
      descriptionKo:
        "폰이 프로모션 랭크에 도달하면 선택 없이 자동으로 비숍이 되며, 인접한 빈 칸(같은 랭크 우선)에 나이트가 배치됩니다. 빈 인접 칸이 없으면 비숍 1개만 배치됩니다. 예: e8에서 프로모션 시 e8에 비숍, d8 또는 f8(빈 경우)에 나이트 배치.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "DUAL_MINOR_PROMOTION",
      },
    },
    {
      id: "infinite_check",
      name: "Infinite Check",
      nameKo: "무한 체크",
      description:
        "Delivering 3 consecutive checks wins the game. Your check counter resets to 0 if you make a move that does not give check. Each player's counter is tracked independently. Example: if white gives check on turns 5, 6, and 7 consecutively, white wins immediately.",
      descriptionKo:
        "연속으로 3회 체크를 주면 승리합니다. 체크하지 않는 수를 두면 카운터가 0으로 초기화됩니다. 양 플레이어의 카운터는 독립적으로 추적됩니다. 예: 백이 5, 6, 7턴에 연속 체크 시 즉시 백이 승리.",
      category: "WIN_CONDITION_MODIFIER",
      targetPieceTypes: [],
      effect: {
        type: "CONSECUTIVE_CHECK_WIN",
        checkCount: 3,
      },
    },
    {
      id: "MN-001",
      name: "Revival",
      nameKo: "부활",
      description:
        "When the first Knight or Bishop belonging to you is captured, a 5-turn countdown begins. After 5 full turns, that piece type revives at its canonical starting square (Knights at b1/g1 for white or b8/g8 for black; Bishops at c1/f1 or c8/f8). If the target square is occupied, the piece appears on the nearest adjacent empty square. Triggers once per game. Example: white Knight captured on turn 3 → Knight revives at b1 or g1 on turn 8.",
      descriptionKo:
        "첫 번째 나이트 또는 비숍이 포획당하면 5턴 카운트다운이 시작됩니다. 5턴 후 해당 기물이 원래 시작 위치(백 나이트: b1/g1, 백 비숍: c1/f1; 흑은 대칭)에 부활합니다. 시작 위치가 점유된 경우 인접한 가장 가까운 빈 칸에 배치됩니다. 게임당 1회 발동. 예: 3턴에 백 나이트가 잡히면 8턴에 b1 또는 g1에 나이트가 부활합니다.",
      category: "DEFENSE",
      targetPieceTypes: ["KNIGHT", "BISHOP"],
      effect: {
        type: "MINOR_REVIVAL",
        delayTurns: 5,
      },
    },
    {
      id: "PN-012",
      name: "Morph Promotion",
      nameKo: "변신 프로모션",
      description:
        "When a pawn promotes, the available promotion types expand to include any enemy piece type you have captured during the game (King excluded). The standard choices (Queen, Rook, Bishop, Knight) are always available. Example: if you have captured a black Rook, your pawn can promote to a Rook in addition to the standard options.",
      descriptionKo:
        "폰 프로모션 시, 이번 게임에서 포획한 적 기물 종류로도 변신 가능합니다 (킹 제외). 기본 선택지(퀸, 룩, 비숍, 나이트)는 항상 유지됩니다. 예: 흑 룩을 한 번이라도 잡았다면, 폰 프로모션 시 룩도 선택 가능합니다.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "MORPH_PROMOTION",
      },
    },
    {
      id: "QN-002",
      name: "Rising Queen",
      nameKo: "부활하는 퀸",
      description:
        "When your Queen is captured, a 10-turn countdown begins. After 10 full turns, the most-advanced remaining pawn automatically promotes to Queen at its current position (no promotion rank required). Triggers once per game. If no pawns remain when the countdown expires, the effect is lost. Example: Queen captured on turn 5 → pawn becomes Queen at the start of turn 16.",
      descriptionKo:
        "퀸이 잡히면 10턴 카운트다운이 시작됩니다. 10턴 후 가장 전진한 폰이 현재 위치에서 자동으로 퀸으로 승급합니다 (프로모션 랭크 불필요). 게임당 1회 발동. 카운트다운 종료 시 폰이 없으면 효과 소멸. 예: 5턴에 퀸이 잡히면 16턴 시작 시 폰이 퀸이 됩니다.",
      category: "PROMOTION_MODIFIER",
      targetPieceTypes: ["QUEEN"],
      effect: {
        type: "RISING_QUEEN",
        delayTurns: 10,
      },
    },
    {
      id: "KN-004",
      name: "Assassin",
      nameKo: "암살자",
      description:
        "A Knight can capture any Queen regardless of immunity effects (Guardian, Still Defense). After capturing a Queen, the Knight becomes permanently weakened. A weakened Knight that captures any non-Queen piece causes mutual destruction: both the Knight and the captured piece are removed from the board. A weakened Knight can still capture Queens normally (without mutual destruction). Example: Knight on f3 captures immune Queen on d4 — the Knight moves to d4 (Queen removed), Knight is now weakened. If the weakened Knight later captures a Bishop, both the Knight and Bishop are removed.",
      descriptionKo:
        "나이트는 면역 효과(수호자, 고요한 수비)를 무시하고 퀸을 포획할 수 있습니다. 퀸을 포획한 나이트는 영구적으로 '약화' 상태가 됩니다. 약화된 나이트가 퀸 이외의 기물을 포획하면 두 기물 모두 제거됩니다(상호 파괴). 약화된 나이트는 퀸은 정상적으로 포획할 수 있습니다. 예: 나이트가 면역 상태의 퀸을 포획 후 약화 → 이후 비숍 포획 시도 시 나이트와 비숍 모두 제거.",
      category: "CAPTURE_MODIFIER",
      targetPieceTypes: ["KNIGHT"],
      effect: {
        type: "ASSASSIN",
      },
    },
    {
      id: "PN-010",
      name: "Last Pawn",
      nameKo: "최후의 폰",
      description:
        "When exactly 1 ally pawn remains on the board, that pawn can always move 2 squares forward from any rank (path must be clear). Example: a pawn on e5 can move to e7 if e6 is empty, even if it has previously moved. Note: this 2-square advance from a non-starting rank does NOT enable en passant. Note: the +40% capture bonus from the original design requires the probability system and is not yet implemented.",
      descriptionKo:
        "아군 폰이 정확히 1개 남으면, 그 폰은 어느 위치에서든 항상 2칸 전진 이동이 가능합니다(경로가 비어 있어야 함). 예: e5의 폰이 e6가 비어 있으면 e7로 이동 가능(이미 움직인 폰도 가능). 주의: 시작 위치 외에서의 2칸 전진은 앙파상 대상이 되지 않습니다. 주의: 원래 설계의 포획 확률 +40% 효과는 확률 시스템 구현 후 추가됩니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["PAWN"],
      effect: {
        type: "LAST_PAWN_MOVEMENT",
      },
    },
    {
      id: "KN-002",
      name: "Agile Knight",
      nameKo: "민첩한 나이트",
      description:
        "Knights can also move 1 square in any of the 8 directions (like a King's step move), in addition to their normal L-shaped jumps. The adjacent-square move follows normal rules: cannot land on an ally piece, can capture enemy pieces. Example: a Knight on e4 can step to e5, f5, f4, f3, e3, d3, d4, or d5, as well as making its normal L-shaped jumps. Useful for escaping corners, approaching slowly, or making precision maneuvers.",
      descriptionKo:
        "나이트가 기존 L자 이동에 더해 8방향으로 1칸 이동(킹처럼)도 가능합니다. 인접 이동은 일반 규칙을 따릅니다: 아군 기물에는 착지 불가, 적 기물 포획 가능. 예: e4의 나이트는 e5, f5, f4, f3, e3, d3, d4, d5로 이동 가능(L자 이동과 병행). 구석 탈출, 천천히 접근하거나 정밀 기동 시 유용합니다.",
      category: "MOVEMENT_MODIFIER",
      targetPieceTypes: ["KNIGHT"],
      effect: {
        type: "ADD_PATTERN",
        pattern: "KING_STEP",
      },
    },
    {
      id: "RK-004",
      name: "Shield Wall",
      nameKo: "방패 벽",
      description:
        "Ally pieces standing strictly between an allied Rook and the allied King on the same rank or file cannot be captured by the opponent. The Rook and King themselves are not protected (they are the wall's endpoints). The immunity disappears the moment the Rook or King moves off the shared line. Example: if your Rook is on h1, King on e1, the pieces on f1 and g1 (between them on rank 1) cannot be captured. Note: KN-004 Assassin (queen-targeting knights) still bypasses this immunity.",
      descriptionKo:
        "같은 랭크 또는 파일에서 아군 룩과 킹 사이에 있는 아군 기물은 상대방이 포획할 수 없습니다. 룩과 킹 자체는 보호되지 않습니다(끝점이기 때문). 룩 또는 킹이 공유 라인을 벗어나는 순간 면역이 사라집니다. 예: 룩이 h1, 킹이 e1에 있으면 f1, g1의 기물은 포획 불가. 주의: KN-004 암살자(퀸 대상 나이트)는 이 면역을 무시합니다.",
      category: "DEFENSE",
      targetPieceTypes: [],
      effect: {
        type: "SHIELD_WALL",
      },
    },
    {
      id: "AL-002",
      name: "Focus",
      nameKo: "집중",
      description:
        "All your pieces gain +5% capture chance. This bonus counters enemy evasion effects — without enemy evasion, captures remain deterministic (100%). Example: if the opponent has 25% evasion, your effective capture chance is 80% instead of 75%.",
      descriptionKo:
        "모든 아군 기물의 포획 확률이 +5% 증가합니다. 이 보너스는 적의 회피 효과를 상쇄합니다 — 적 회피가 없으면 포획은 여전히 확정적(100%)입니다. 예: 상대가 25% 회피를 가지면, 실효 포획 확률은 75% 대신 80%입니다.",
      category: "CAPTURE_MODIFIER",
      targetPieceTypes: [],
      effect: {
        type: "CAPTURE_MODIFIER",
        modifier: 0.05,
      },
    },
    {
      id: "AL-006",
      name: "Evasion",
      nameKo: "회피",
      description:
        "All your pieces have a 25% chance to evade capture. When an opponent's piece attempts to capture one of your pieces, there is a 25% probability that the capture fails and the opponent must choose a different move. Example: an opponent's Rook tries to capture your Bishop — there is a 25% chance the Bishop evades and the Rook's move is cancelled.",
      descriptionKo:
        "모든 아군 기물에 25% 회피 확률이 부여됩니다. 상대 기물이 아군 기물을 포획하려 할 때 25% 확률로 포획이 실패하며 상대는 다른 수를 선택해야 합니다. 예: 상대 룩이 아군 비숍을 잡으려 할 때 25% 확률로 비숍이 회피하고 룩의 이동이 취소됩니다.",
      category: "DEFENSE",
      targetPieceTypes: [],
      effect: {
        type: "DEFENSE_MODIFIER",
        evasion: 0.25,
      },
    },
    {
      id: "KN-003",
      name: "Knight's Oath",
      nameKo: "기사의 맹세",
      description:
        "Knights adjacent to your King (within 1 square) gain +25% capture chance. This bonus only applies when the Knight is next to the King and counters enemy evasion. Without enemy evasion, captures remain deterministic. Example: a Knight on f1 next to King on e1 has 100% capture chance against 25% evasion (instead of 75%).",
      descriptionKo:
        "킹 인접(1칸 이내) 나이트의 포획 확률이 +25% 증가합니다. 나이트가 킹 옆에 있을 때만 적용되며 적 회피를 상쇄합니다. 적 회피가 없으면 포획은 확정적입니다. 예: 킹(e1) 옆 나이트(f1)는 25% 회피 상대에게 100% 포획 확률을 가집니다 (75% 대신).",
      category: "CAPTURE_MODIFIER",
      targetPieceTypes: ["KNIGHT"],
      effect: {
        type: "CAPTURE_MODIFIER",
        modifier: 0.25,
        condition: {
          type: "ADJACENT_TO_ALLY_KING",
          radius: 1,
        },
      },
    },
    {
      id: "QN-001",
      name: "Queen's Majesty",
      nameKo: "퀸의 위엄",
      description:
        "While your original Queen is alive, all your pieces gain +5% capture chance. If your original Queen is captured, all your pieces suffer -10% capture chance instead. Pawn-to-Queen promotion does NOT restore the bonus. Example: with Queen alive and 25% enemy evasion, your capture chance is 80%. With Queen dead, it drops to 65%.",
      descriptionKo:
        "원래 퀸이 살아 있는 동안 모든 아군 기물의 포획 확률 +5%. 원래 퀸이 잡히면 모든 기물의 포획 확률 -10%. 폰→퀸 승급으로는 보너스가 복원되지 않습니다. 예: 퀸 생존 시 적 회피 25% → 포획 80%. 퀸 사망 시 → 포획 65%.",
      category: "CAPTURE_MODIFIER",
      targetPieceTypes: [],
      effect: {
        type: "CAPTURE_MODIFIER",
        modifier: 0.05,
        modifierWhenFalse: -0.1,
        condition: {
          type: "PIECE_ALIVE",
          pieceType: "QUEEN",
          whenAlive: true,
        },
      },
    },
  ].map((aug) => [aug.id, aug as Augmentation]),
);

/**
 * Get an augmentation by ID
 */
export function getAugmentation(id: string): Augmentation | undefined {
  return AUGMENTATION_REGISTRY.get(id);
}

/**
 * Get all augmentation IDs
 */
export function getAllAugmentationIds(): string[] {
  return Array.from(AUGMENTATION_REGISTRY.keys());
}

/**
 * Get random augmentation choices
 * @param count Number of choices to return (default 3)
 * @param exclude IDs to exclude from selection
 * @returns Array of augmentation IDs
 */
export function getRandomAugmentationChoices(
  count = 3,
  exclude: string[] = [],
): string[] {
  const available = getAllAugmentationIds().filter(
    (id) => !exclude.includes(id),
  );

  // Fisher-Yates shuffle
  const shuffled = [...available];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }

  return shuffled.slice(0, Math.min(count, shuffled.length));
}
